import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NativeCheckbox } from '@/components/ui/NativeCheckbox';
import { toast } from 'sonner';
import { api } from '@/App';
import Navbar from '@/components/Navbar';
import { usePhotoPreview } from '@/components/PhotoPreview';
import { COUNTRIES } from '@/data/countries';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { StandingMovement } from '@/components/StandingMovement';

// Helper function to get full image URL
const getImageUrl = (photoUrl) => {
  if (!photoUrl) return null;
  // If it's a base64 data URL, return as is
  if (photoUrl.startsWith('data:image/')) {
    return photoUrl;
  }
  if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
    return photoUrl;
  }
  const backendUrl = window.location.origin || '';
  return `${backendUrl}${photoUrl}`;
};

const POSITIONS = [
  'Libero',
  'Outside Hitter',
  'Middle Blocker',
  'Opposite Hitter',
  'Setter'
];

const GENDER_OPTIONS = [
  'Male',
  'Female',
  'Non-Binary/Other',
  'Prefer not to say'
];

const LEVELS = [
  '6th class',
  '5th class',
  '4th class',
  '3rd class',
  '2nd class',
  '1st class',
  'Promotion class',
  '2nd division',
  '1st division',
  'Other'
];

export default function ProfilePage({ user, onLogout, onUpdateUser }) {
  const previewPhoto = usePhotoPreview();
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ratingHistory, setRatingHistory] = useState([]);
  const [standing, setStanding] = useState(null);
  const [participates, setParticipates] = useState(true);

  // Fetch community standing when component mounts or user changes
  useEffect(() => {
    const fetchStanding = async () => {
      if (!user?.id) return;
      try {
        const response = await api.get(`/users/${user.id}/standing`);
        setStanding(response.data);
      } catch (error) {
        console.error('Failed to fetch standing:', error);
      }
    };
    const fetchParticipation = async () => {
      try {
        const response = await api.get('/user/rating-participation');
        setParticipates(response.data.enabled !== false);
      } catch (error) {
        console.error('Failed to fetch rating participation:', error);
      }
    };
    fetchParticipation();
    fetchStanding();
  }, [user]);
  
  // Fetch rating history when component mounts or user changes
  useEffect(() => {
    const fetchRatingHistory = async () => {
      if (user?.id && user?.community_rating_average > 0) {
        try {
          const response = await api.get(`/users/${user.id}/rating-history`);
          // Reverse to show oldest first (left to right progression)
          const historyData = response.data.history.reverse().map((game, index) => ({
            name: game.game_date, // Show date instead of game name
            rating: game.rating,
            fullName: game.game_title,
            date: game.game_date
          }));
          setRatingHistory(historyData);
        } catch (error) {
          console.error('Failed to fetch rating history:', error);
        }
      }
    };
    
    fetchRatingHistory();
  }, [user]);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    preferred_name: user?.preferred_name || '',
    phone: user?.phone || '',
    date_of_birth: user?.date_of_birth || '',
    gender: user?.gender || '',
    volleyball_experience: user?.volleyball_experience || '',
    position_1st: user?.position_1st || '',
    position_2nd: user?.position_2nd || '',
    position_3rd: user?.position_3rd || '',
    current_level: user?.current_level || '',
    current_level_other: user?.current_level_other || '',
    highest_level: user?.highest_level || '',
    highest_level_other: user?.highest_level_other || '',
    where_hear_about_us: user?.where_hear_about_us || '',
    club_experience: user?.club_experience || false,
    club_names: user?.club_names || '',
    // Pre-fill current_club from legacy club_names if not yet set (one-time migration)
    current_club: user?.current_club ?? (user?.club_names || ''),
    previous_clubs: user?.previous_clubs || '',
    preferred_positions: user?.preferred_positions || [],
    gdpr_consent: user?.gdpr_consent || false,
    country_of_origin: user?.country_of_origin || '',
    current_country: user?.current_country || '',
    current_city: user?.current_city || ''
  });

  const handlePositionToggle = (position) => {
    const current = formData.preferred_positions || [];
    if (current.includes(position)) {
      setFormData({...formData, preferred_positions: current.filter(p => p !== position)});
    } else {
      setFormData({...formData, preferred_positions: [...current, position]});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate profile photo (MANDATORY)
    if (!user?.profile_photo_url) {
      toast.error('Profile photo is required. Please upload your photo before saving.');
      return;
    }

    // Validate that 1st, 2nd and 3rd preferred positions are all different
    const chosenPositions = [formData.position_1st, formData.position_2nd, formData.position_3rd].filter(Boolean);
    if (new Set(chosenPositions).size !== chosenPositions.length) {
      toast.error('Please choose a different position for each preference (1st, 2nd, 3rd).');
      return;
    }
    
    setLoading(true);

    try {
      const response = await api.put('/users/me', formData);
      toast.success('Profile updated successfully!');
      onUpdateUser(response.data);
      setIsEditMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  // VIEW MODE
  if (!isEditMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        
        <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-8 md:p-12">
              {/* Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900\">My Profile</h1>
                <Button
                  onClick={() => setIsEditMode(true)}
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold px-6 sm:px-8 py-3 rounded-full text-sm sm:text-base"
                >
                  ✏️ Edit Profile
                </Button>
              </div>

              {/* Profile Photo */}
              {user?.profile_photo_url && (
                <div className="flex justify-center mb-6 sm:mb-8">
                  <img 
                    src={getImageUrl(user.profile_photo_url)} 
                    alt="Profile" 
                    className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-orange-500 shadow-lg" 
                  />
                </div>
              )}

              {/* Rating participation disabled note */}
              {!participates && (
                <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6" data-testid="rating-participation-disabled-note">
                  <h3 className="text-base sm:text-lg font-bold text-gray-800">Rating participation is disabled</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Your rating, kudos and standing are hidden from players and kept privately (visible only to admins).
                    You can re-enable rating participation in <a href="/settings" className="underline font-semibold text-orange-600">Settings</a>.
                  </p>
                </div>
              )}

              {/* Rating Display */}
              {participates && user?.rating > 0 && (
                <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-center sm:text-left">
                      <h3 className="text-base sm:text-lg font-bold text-purple-900">Player Rating</h3>
                      <p className="text-xs sm:text-sm text-purple-600 mt-1">Set by admins based on skill level</p>
                    </div>
                    <div className="text-4xl sm:text-5xl font-bold text-purple-700">{user.rating}/10</div>
                  </div>
                </div>
              )}

              {/* Community Stats */}
              {participates && user?.community_rating_average > 0 && (
                <>
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 sm:p-6 mb-4">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                      <div className="text-center sm:text-left">
                        <h3 className="text-base sm:text-lg font-bold text-green-900">Community Rating</h3>
                        <p className="text-xs sm:text-sm text-green-600 mt-1">Based on {user.total_games_rated || 0} games rated</p>
                      </div>
                      <div className="text-4xl sm:text-5xl font-bold text-green-700">{user.community_rating_average.toFixed(2)}/10</div>
                    </div>
                  </div>

                  {/* Rating Progress Chart */}
                  {ratingHistory.length > 0 && (
                    <div className="bg-white border-2 border-green-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                      <h3 className="text-base sm:text-lg font-bold text-green-900 mb-4">Rating Progress</h3>
                      <p className="text-xs sm:text-sm text-gray-600 mb-4">Your performance over the last {ratingHistory.length} game{ratingHistory.length > 1 ? 's' : ''}</p>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={ratingHistory} margin={{ top: 30, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            dataKey="name" 
                            tick={{ fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis 
                            domain={[0, 10]} 
                            ticks={[0, 2, 4, 6, 8, 10]}
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload[0]) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white p-3 border-2 border-green-200 rounded-lg shadow-lg">
                                    <p className="font-bold text-green-900">{data.fullName}</p>
                                    <p className="text-sm text-gray-600">{data.date}</p>
                                    <p className="text-lg font-bold text-green-700 mt-1">Rating: {data.rating}/10</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="rating" 
                            stroke="#15803d" 
                            strokeWidth={3}
                            dot={{ fill: '#15803d', r: 6 }}
                            activeDot={{ r: 8 }}
                            label={({ value, x, y }) => (
                              <text 
                                x={x} 
                                y={y - 10} 
                                fill="#15803d" 
                                fontSize="14" 
                                fontWeight="bold" 
                                textAnchor="middle"
                              >
                                {value}
                              </text>
                            )}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </>
              )}

              {/* Community Standing */}
              {participates && standing?.rank && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6" data-testid="profile-standing">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-center sm:text-left">
                      <h3 className="text-base sm:text-lg font-bold text-amber-900">Standing</h3>
                      <p className="text-xs sm:text-sm text-amber-600 mt-1">Your rank in the community by rating</p>
                    </div>
                    <div className="text-3xl sm:text-4xl font-bold text-amber-700 flex items-center gap-2">
                      <span>{standing.rank_label} <span className="text-xl sm:text-2xl text-amber-500">/ {standing.total}</span></span>
                      <StandingMovement standing={standing} className="text-base" />
                    </div>
                  </div>
                </div>
              )}

              {/* Profile Information Grid */}
              <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                {/* Personal Information */}
                <div className="space-y-3 sm:space-y-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 border-b-2 border-orange-500 pb-2">Personal Information</h2>
                  
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">Full Name</p>
                    <p className="text-base sm:text-lg text-gray-900 font-semibold\">{user?.first_name} {user?.last_name}</p>
                  </div>

                  {user?.preferred_name && (
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Preferred Name</p>
                      <p className="text-lg text-gray-900 font-semibold">{user.preferred_name}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm text-gray-500 font-medium">Email</p>
                    <p className="text-lg text-gray-900">{user?.email}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 font-medium">Phone</p>
                    <p className="text-lg text-gray-900">{user?.phone || 'Not provided'}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 font-medium">Date of Birth</p>
                    <p className="text-lg text-gray-900">{user?.date_of_birth || 'Not provided'}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 font-medium">Gender</p>
                    <p className="text-lg text-gray-900">{user?.gender || 'Not provided'}</p>
                  </div>
                </div>

                {/* Volleyball Information */}
                <div className="space-y-3 sm:space-y-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 border-b-2 border-orange-500 pb-2">Volleyball Information</h2>
                  
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">Experience Level</p>
                    <p className="text-base sm:text-lg text-gray-900 font-semibold">{user?.volleyball_experience || 'Not provided'}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 font-medium">Favorite Positions</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {user?.position_1st && (
                        <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                          1st: {user.position_1st}
                        </span>
                      )}
                      {user?.position_2nd && (
                        <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                          2nd: {user.position_2nd}
                        </span>
                      )}
                      {user?.position_3rd && (
                        <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
                          3rd: {user.position_3rd}
                        </span>
                      )}
                    </div>
                  </div>

                  {user?.current_level && (
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Current Level</p>
                      <p className="text-lg text-gray-900">
                        {user.current_level === 'Other' ? user.current_level_other : user.current_level}
                      </p>
                    </div>
                  )}

                  {user?.highest_level && (
                    <div>
                      <p className="text-sm text-gray-500 font-medium">Highest Level</p>
                      <p className="text-lg text-gray-900">
                        {user.highest_level === 'Other' ? user.highest_level_other : user.highest_level}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm text-gray-500 font-medium">Club Experience</p>
                    <p className="text-lg text-gray-900">{user?.club_experience ? 'Yes' : 'No'}</p>
                    {user?.club_experience && (user?.current_club || user?.club_names) && (
                      <p className="text-md text-gray-700 mt-1">
                        <span className="font-semibold">Current:</span> {user.current_club || user.club_names}
                      </p>
                    )}
                    {user?.club_experience && user?.previous_clubs && (
                      <p className="text-md text-gray-700 mt-1">
                        <span className="font-semibold">Previous:</span> {user.previous_clubs}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-orange-500 pb-2">Location</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Country of Origin</p>
                    <p className="text-lg text-gray-900">{user?.country_of_origin || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Current Country</p>
                    <p className="text-lg text-gray-900">{user?.current_country || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Current City</p>
                    <p className="text-lg text-gray-900">{user?.current_city || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              {user?.where_hear_about_us && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-orange-500 pb-2">Additional</h2>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">How did you hear about us?</p>
                    <p className="text-lg text-gray-900">{user.where_hear_about_us}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // EDIT MODE
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900">Edit Profile</h1>
              <Button
                onClick={() => setIsEditMode(false)}
                variant="outline"
                className="font-semibold"
              >
                ← Back to View
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {participates && user?.rating > 0 && (
                <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                  <Label className="text-purple-900 font-bold">Your Player Rating</Label>
                  <div className="mt-2 text-3xl font-bold text-purple-700">{user.rating} / 10</div>
                  <p className="text-xs text-purple-600 mt-2">Your rating is set by admins based on your skill level</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="preferred_name">Preferred Name (Optional)</Label>
                <Input
                  id="preferred_name"
                  value={formData.preferred_name}
                  onChange={(e) => setFormData({...formData, preferred_name: e.target.value})}
                  className="mt-2"
                  placeholder="If different from first name"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="date_of_birth">Date of Birth *</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    required
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-2"
                    placeholder="+31..."
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="gender">Gender *</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => setFormData({...formData, gender: value})}
                >
                  <SelectTrigger className="mt-2" id="gender">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {GENDER_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Helps us balance teams</p>
              </div>

              <div>
                <Label htmlFor="profile_photo_upload">
                  Profile Photo * 
                  {!user?.profile_photo_url && (
                    <span className="text-sm font-normal text-red-600"> (Required - Please upload to continue)</span>
                  )}
                  {user?.profile_photo_url && (
                    <span className="text-sm font-normal text-gray-500"> (Update if needed - Max 5MB)</span>
                  )}
                </Label>
                <div className="mt-2 mb-2 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                  <p className="text-sm text-blue-900 font-semibold mb-1">📸 Photo guidelines</p>
                  <ul className="text-xs text-blue-800 list-disc pl-5 space-y-0.5">
                    <li>Use a clear, well-lit <strong>face photo</strong> so teammates and admins can recognize you.</li>
                    <li>Your face should be fully visible — no sunglasses, hats, masks, or heavy filters.</li>
                    <li>Centered headshot on a plain background works best.</li>
                    <li>No group photos, avatars, logos, memes, pets, or landscape pictures.</li>
                    <li>Formats: JPG, PNG, or WebP. Max 5 MB.</li>
                  </ul>
                </div>
                {user?.profile_photo_url && (
                  <div className="mt-2 mb-2">
                    <img src={getImageUrl(user.profile_photo_url)} alt="Current photo" className="w-20 h-20 rounded-full object-cover border-2 border-gray-300" />
                    <p className="text-xs text-gray-500 mt-1">Current photo</p>
                  </div>
                )}
                {!user?.profile_photo_url && (
                  <div className="mt-2 mb-2 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                    <p className="text-sm text-red-600 font-medium">⚠️ Profile photo is required</p>
                    <p className="text-xs text-red-500 mt-1">Please upload your photo to save your profile</p>
                  </div>
                )}
                <Input
                  id="profile_photo_upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('File size must be less than 5MB');
                        e.target.value = '';
                        return;
                      }
                      try {
                        const photoFormData = new FormData();
                        photoFormData.append('file', file);
                        await api.post('/users/upload-photo', photoFormData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        toast.success('Photo uploaded successfully!');
                        // Reload page to show new photo
                        window.location.reload();
                      } catch (error) {
                        toast.error('Failed to upload photo');
                      }
                    }
                  }}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">Upload jpg, png, or webp (max 5MB)</p>
              </div>

              <div>
                <Label htmlFor="volleyball_experience">Volleyball Experience *</Label>
                <Select
                  value={formData.volleyball_experience}
                  onValueChange={(value) => setFormData({...formData, volleyball_experience: value})}
                >
                  <SelectTrigger className="mt-2" id="volleyball_experience">
                    <SelectValue placeholder="Select your experience level" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate (1-3 years)">Intermediate (1-3 years)</SelectItem>
                    <SelectItem value="Upper Intermediate (4-6 years)">Upper Intermediate (4-6 years)</SelectItem>
                    <SelectItem value="Advanced (7+ years)">Advanced (7+ years)</SelectItem>
                    <SelectItem value="Professional (played in local or national leagues)">Professional (played in local or national leagues)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="position_1st">1st Favorite Position *</Label>
                <Select
                  value={formData.position_1st}
                  onValueChange={(value) => setFormData({...formData, position_1st: value})}
                >
                  <SelectTrigger className="mt-2" id="position_1st">
                    <SelectValue placeholder="Select your 1st favorite position" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {POSITIONS.map((pos) => (
                      <SelectItem
                        key={pos}
                        value={pos}
                        disabled={pos !== formData.position_1st && (pos === formData.position_2nd || pos === formData.position_3rd)}
                      >
                        {pos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="position_2nd">2nd Favorite Position (Optional)</Label>
                <Select
                  value={formData.position_2nd}
                  onValueChange={(value) => setFormData({...formData, position_2nd: value})}
                >
                  <SelectTrigger className="mt-2" id="position_2nd">
                    <SelectValue placeholder="Select your 2nd favorite position" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {POSITIONS.map((pos) => (
                      <SelectItem
                        key={pos}
                        value={pos}
                        disabled={pos !== formData.position_2nd && (pos === formData.position_1st || pos === formData.position_3rd)}
                      >
                        {pos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="position_3rd">3rd Favorite Position (Optional)</Label>
                <Select
                  value={formData.position_3rd}
                  onValueChange={(value) => setFormData({...formData, position_3rd: value})}
                >
                  <SelectTrigger className="mt-2" id="position_3rd">
                    <SelectValue placeholder="Select your 3rd favorite position" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {POSITIONS.map((pos) => (
                      <SelectItem
                        key={pos}
                        value={pos}
                        disabled={pos !== formData.position_3rd && (pos === formData.position_1st || pos === formData.position_2nd)}
                      >
                        {pos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Have you ever played in a volleyball club? *</Label>
                <div className="flex gap-4 mt-2">
                  <div className="flex items-center space-x-2">
                    <NativeCheckbox
                      id="club-yes"
                      checked={formData.club_experience === true}
                      onChange={(checked) => setFormData({...formData, club_experience: true})}
                    />
                    <label htmlFor="club-yes" className="text-sm font-medium cursor-pointer">Yes</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <NativeCheckbox
                      id="club-no"
                      checked={formData.club_experience === false}
                      onChange={(checked) => setFormData({...formData, club_experience: false})}
                    />
                    <label htmlFor="club-no" className="text-sm font-medium cursor-pointer">No</label>
                  </div>
                </div>
              </div>

              {formData.club_experience && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="current_club">Current Club</Label>
                    <Input
                      id="current_club"
                      value={formData.current_club}
                      onChange={(e) => setFormData({...formData, current_club: e.target.value})}
                      className="mt-2"
                      placeholder="e.g., Amsterdam Volleyball Club"
                    />
                  </div>
                  <div>
                    <Label htmlFor="previous_clubs">Previous Clubs (optional)</Label>
                    <Input
                      id="previous_clubs"
                      value={formData.previous_clubs}
                      onChange={(e) => setFormData({...formData, previous_clubs: e.target.value})}
                      className="mt-2"
                      placeholder="e.g., Rotterdam VC, Utrecht Smash — separate with commas"
                    />
                  </div>
                </div>
              )}

              {/* Conditional Level Fields */}
              {formData.volleyball_experience && formData.volleyball_experience !== 'Beginner' && (
                <>
                  <div>
                    <Label htmlFor="current_level">Current Level</Label>
                    <Select
                      value={formData.current_level}
                      onValueChange={(value) => setFormData({...formData, current_level: value})}
                    >
                      <SelectTrigger className="mt-2" id="current_level">
                        <SelectValue placeholder="Select your current level" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {LEVELS.map((level) => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.current_level === 'Other' && (
                    <div>
                      <Label htmlFor="current_level_other">Specify Current Level *</Label>
                      <Input
                        id="current_level_other"
                        value={formData.current_level_other}
                        onChange={(e) => setFormData({...formData, current_level_other: e.target.value})}
                        className="mt-2"
                        placeholder="e.g., Regional league"
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="highest_level">Highest Level Ever Played</Label>
                    <Select
                      value={formData.highest_level}
                      onValueChange={(value) => setFormData({...formData, highest_level: value})}
                    >
                      <SelectTrigger className="mt-2" id="highest_level">
                        <SelectValue placeholder="Select your highest level" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {LEVELS.map((level) => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.highest_level === 'Other' && (
                    <div>
                      <Label htmlFor="highest_level_other">Specify Highest Level *</Label>
                      <Input
                        id="highest_level_other"
                        value={formData.highest_level_other}
                        onChange={(e) => setFormData({...formData, highest_level_other: e.target.value})}
                        className="mt-2"
                        placeholder="e.g., National team"
                      />
                    </div>
                  )}
                </>
              )}

              <div>
                <Label htmlFor="where_hear_about_us">Where Did You Hear About Us?</Label>
                <Input
                  id="where_hear_about_us"
                  value={formData.where_hear_about_us}
                  onChange={(e) => setFormData({...formData, where_hear_about_us: e.target.value})}
                  className="mt-2"
                  placeholder="e.g., Friend, Instagram, Google..."
                />
              </div>

              <div>
                <Label htmlFor="country_of_origin">Country of Origin</Label>
                <Select
                  value={formData.country_of_origin}
                  onValueChange={(value) => setFormData({...formData, country_of_origin: value})}
                >
                  <SelectTrigger className="mt-2" id="country_of_origin">
                    <SelectValue placeholder="Select your country of origin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="current_country">Current Country</Label>
                <Select
                  value={formData.current_country}
                  onValueChange={(value) => setFormData({...formData, current_country: value})}
                >
                  <SelectTrigger className="mt-2" id="current_country">
                    <SelectValue placeholder="Select your current country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="current_city">Current City</Label>
                <Input
                  id="current_city"
                  value={formData.current_city}
                  onChange={(e) => setFormData({...formData, current_city: e.target.value})}
                  className="mt-2"
                  placeholder="e.g., Amsterdam"
                />
              </div>

              <div className="pt-4 flex gap-4">
                <Button
                  type="button"
                  onClick={() => setIsEditMode(false)}
                  variant="outline"
                  className="flex-1 py-6 text-lg rounded-full font-semibold"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-6 text-lg rounded-full"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
